import React, { useState, useEffect } from 'react';
import EPaperList from './components/EPaperList';
import EPaperViewer from './components/EPaperViewer';
import BackToTop from './components/BackToTop';
import WhatsAppQR from './components/WhatsAppQR';
import SocialMedia from './components/SocialMedia';
import SEO from './components/SEO';
import { EPaper } from './types/epaper';
import { loadEPapers } from './services/epaperService';
import { MessageCircle } from 'lucide-react';
import './App.css';

function App() {
  const [epapers, setEpapers] = useState<EPaper[]>([]);
  const [selectedEPaper, setSelectedEPaper] = useState<EPaper | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showWhatsAppQR, setShowWhatsAppQR] = useState<boolean>(false);

  useEffect(() => {
    const fetchEPapers = async () => {
      try {
        setLoading(true);
        const data = await loadEPapers();
        setEpapers(data);
      } catch (err) {
        setError('Failed to load E-Papers. Please try again later.');
        console.error('Error fetching E-Papers:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEPapers();
  }, []);

  const handleSelectEPaper = (epaper: EPaper) => {
    setSelectedEPaper(epaper);
  };

  const handleCloseViewer = () => {
    setSelectedEPaper(null);
  };

  const specialEditions = epapers.filter(epaper => epaper.type === 'special');
  const regularEpapers = epapers.filter(epaper => epaper.type === 'regular');

  if (error) {
    return (
      <div className="app-error">
        <div className="error-container">
          <h2>Something went wrong</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <SEO 
        title="Namibia Mining & Energy E-Paper Archive | Mining & Energy News"
        description="Complete digital archive of Namibia Mining & Energy newspaper editions. Browse historical mining, energy and resources news. Namibia's leading mining and energy publication."
        keywords="Namibia mining news, Mining & Energy e-paper, Namibia energy news, mining industry Namibia, energy sector Namibia, resources news, digital newspaper archive"
        url="https://epaper.miningandenergy.com.na/"
        image="https://epaper.miningandenergy.com.na/logo.png"
        author="Mining & Energy"
        tags={["Namibia", "Mining", "Energy", "Resources", "Windhoek", "E-Paper", "Digital Newspaper", "Mining News"]}
      />
      <header className="app-header">
        <div className="container">
          <div className="header-content">
            <div className="header-logo-section">
              <a href="https://miningandenergy.com.na" target="_blank" rel="noopener noreferrer" className="logo-link">
                <img src="/logo.png" alt="Namibia Mining & Energy Logo" className="logo-image" />
              </a>
            </div>
            <div className="header-actions">
              <SocialMedia />
              <button 
                className="whatsapp-qr-button"
                onClick={() => setShowWhatsAppQR(true)}
                title="Follow us on WhatsApp"
              >
                <MessageCircle size={20} />
                <span>WhatsApp</span>
              </button>
            </div>
          </div>
        </div>
      </header>
      
      <main className="app-main">
        <div className="container">
          <EPaperList
            epapers={regularEpapers}
            specialEditions={specialEditions}
            onSelectEPaper={handleSelectEPaper}
            loading={loading}
          />
        </div>
      </main>
      
      {selectedEPaper && (
        <EPaperViewer
          epaper={selectedEPaper}
          onClose={handleCloseViewer}
        />
      )}
      
      {showWhatsAppQR && (
        <WhatsAppQR 
          isOpen={showWhatsAppQR}
          onClose={() => setShowWhatsAppQR(false)}
        />
      )}
      
      <footer className="app-footer">
        <div className="container">
          <p>&copy; {new Date().getFullYear()} Mining & Energy | All Rights Reserved</p>
        </div>
      </footer>

      <BackToTop />
    </div>
  );
}

export default App;
